import { useNavigate } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatCurrency, formatDateTime } from "@/lib/constants";
import { DeliverableBadge, PaymentBadge } from "@/components/Badges";
import { Camera, AlertTriangle, History } from "lucide-react";
import { ClientAvatar } from "@/components/ClientAvatar";

export default function OrdersTable({ orders }) {
  const navigate = useNavigate();

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card/30" data-testid="orders-table">
      <Table>
        <TableHeader>
          <TableRow className="border-border hover:bg-transparent">
            <TableHead className="overline">Order</TableHead>
            <TableHead className="overline">Client</TableHead>
            <TableHead className="overline">Shoot</TableHead>
            <TableHead className="overline">Products</TableHead>
            <TableHead className="overline">Progress</TableHead>
            <TableHead className="overline">Payment</TableHead>
            <TableHead className="overline">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((o) => {
            const tracked = o.shoot_cancelled ? [] : (o.deliverables || []).filter((d) => d.due_source !== "excluded");
            const types = [...new Set(tracked.map((d) => d.type))].slice(0, 3);
            const done = tracked.filter((d) => d.status === "DELIVERED").length;
            const total = tracked.length;
            const pct = total ? (done / total) * 100 : 0;
            return (
              <TableRow
                key={o.id}
                data-testid={`order-row-${o.id}`}
                className="cursor-pointer border-border transition-colors hover:bg-secondary/40"
                onClick={() => navigate(`/orders/${o.id}`)}
              >
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium leading-tight">{o.address || o.title}</span>
                    {o.is_reshoot && (
                      <span title="Reshoot — property has multiple shoots" data-testid={`reshoot-badge-${o.id}`}
                        className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
                        <History className="h-2.5 w-2.5" /> Reshoot
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground">#{o.number}</div>
                </TableCell>
                <TableCell className="text-sm">
                  <span className="flex items-center gap-2">{o.client_name && <ClientAvatar name={o.client_name} email={o.client_email} className="h-6 w-6 text-[9px]" />}{o.client_name || "—"}</span>
                </TableCell>
                <TableCell>
                  {o.shoot_cancelled ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[11px] font-medium text-rose-600 dark:text-rose-300" data-testid={`shoot-cancelled-${o.id}`}>
                      <AlertTriangle className="h-3 w-3" /> Shoot cancelled
                    </span>
                  ) : o.shoot_date ? (
                    <div>
                      <div className="text-sm font-medium">{formatDateTime(o.shoot_date)}</div>
                      {o.photographers?.length > 0 && (
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Camera className="h-3 w-3" />{o.photographers.join(", ")}
                        </div>
                      )}
                    </div>
                  ) : <span className="text-xs text-muted-foreground">Unscheduled</span>}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {types.map((t) => <DeliverableBadge key={t} type={t} />)}
                    {tracked.length > 3 && (
                      <span className="text-[11px] text-muted-foreground">+{tracked.length - 3}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2" data-testid={`progress-${o.id}`}>
                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary">
                      <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">{done}/{total}</span>
                  </div>
                </TableCell>
                <TableCell><PaymentBadge status={o.payment_status} /></TableCell>
                <TableCell className="font-mono text-sm">{formatCurrency(o.total_amount, o.currency)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
